import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { HttpErrorResponse } from "@angular/common/http";
import { ButtonComponent, ButtonVariant } from "@sxm-content-creators/tools-ds";
import { SnackbarService } from "../../../../components/snackbar/snackbar.service";
import { SubmissionFreeformApiService } from "../../services/submission-freeform.api.service";
import { InteractiveListItem } from "../../../models/interactives-list.model";

export interface MoveSubmissionModalData {
    submissionIds: string[];
    interactiveOptions: InteractiveListItem[];
}

@Component({
    selector: "app-move-submission-modal",
    templateUrl: "./move-submission-modal.component.html",
    styleUrl: "./move-submission-modal.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ReactiveFormsModule, MatFormFieldModule, MatSelectModule, ButtonComponent]
})
export class MoveSubmissionModalComponent {
    readonly data = inject<MoveSubmissionModalData>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(MatDialogRef<MoveSubmissionModalComponent, boolean>);
    private readonly submissionFreeformApi = inject(SubmissionFreeformApiService);
    private readonly snackbar = inject(SnackbarService);

    readonly interactiveControl = new FormControl<string | null>(null, Validators.required);
    readonly isSubmitting = signal(false);

    moveSubmissions(): void {
        if (this.interactiveControl.invalid || this.isSubmitting()) return;

        const targetInteractiveId = this.interactiveControl.value!;
        const { submissionIds } = this.data;
        const suffix = submissionIds.length > 1 ? "s" : "";

        this.isSubmitting.set(true);
        this.interactiveControl.disable();
        // Block ESC/backdrop dismissal while the request is in flight; close()/X are already
        // gated above, and re-enabled in onMoveError() if the request doesn't succeed outright.
        this.dialogRef.disableClose = true;

        this.submissionFreeformApi.moveToInteractive({ submissionIds, targetInteractiveId }).subscribe({
            next: response => {
                const failures = response.results.filter(result => result.status === "FAILURE");
                const movedSubmissions = response.results.filter(result => result.status === "SUCCESS");

                if (failures.length > 0) {
                    if (movedSubmissions.length > 0) {
                        // The only per-id failure bulk-move reports is "submission not found" (e.g. concurrently
                        // deleted by another user), safe to disregard rather than block the rest of the move.
                        console.warn(
                            `${submissionIds.length > 1 ? "Some submissions" : "A submission"} could not be moved:`,
                            failures
                        );
                    } else {
                        this.snackbar.show(`We couldn't move the submission${suffix}.`, "error");
                    }
                }

                if (movedSubmissions.length) {
                    this.snackbar.show(`Submission${suffix} Moved.`, "success");
                }
                this.dialogRef.close(true);
            },
            error: (error: HttpErrorResponse) =>
                this.onMoveError(error.error?.detail ?? `We couldn't move the submission${suffix}.`)
        });
    }

    close(): void {
        if (this.isSubmitting()) return;
        this.dialogRef.close();
    }

    private onMoveError(message: string): void {
        this.isSubmitting.set(false);
        this.interactiveControl.enable();
        this.dialogRef.disableClose = false;
        this.snackbar.show(message, "error");
    }

    protected readonly ButtonVariant = ButtonVariant;
}
